import {
  USER_LOGIN,
  USER_LOGOUT,
  USER_UPDATE_ROLES,
  USER_UPDATE_USER,
  USER_UPDATE_JWT,
  USER_UPDATE_REGISTER_INFO
} from '@/store/mutypes';
import {
  RootState,
  UserState,
  UpdatePasswordDTO,
  RegisterDTO,
  JoinDTO,
  ResetDTO,
  RegisterResponse,
  RegisterInfo,
  SubscribeDTO,
  Roles,
  UserGetters
} from '@/types';
import { ActionContext } from 'vuex';
import { LocalStorageService, authSvc, userSvc } from '@/services';
import {
  User,
  Jwt,
  JwtDTO,
  Role,
  SelectOption,
  Response,
  Permissions
} from '@/types';
import router from '@/router';
import { Message } from 'element-ui';

// initial state
const modState: UserState = {
  user: null,
  jwt: null,
  registerInfo: null,
  roles: []
};

// getters
const getters = {
  getJwtToken: (state: UserState): string | null => {
    return state.jwt ? state.jwt.token : null;
  },
  getRoleOptions: (state: UserState): SelectOption[] => {
    return state.roles.map((r: Role) => ({
      value: r.id,
      text: r.name
    }));
  },
  isMe: (state: UserState) => (uid: number): boolean => {
    return state.user!.id === uid;
  },
  hasRole: (state: UserState) => (role: string): boolean => {
    return (
      state.user !== null &&
      state.user.roles !== null &&
      state.user.roles!.some(r => r.name === role)
    );
  },
  hasPermission: (state: UserState, getters: UserGetters) => (
    permission: string
  ): boolean => {
    return (
      getters.hasRole(Roles.ROLE_SERVICE_ADMIN) ||
      (state.user !== null &&
        state.user.permissions !== null &&
        state.user.permissions!.some(p => p.name === permission))
    );
  }
};

// actions, to handle async call
const actions = {
  /**
   * login action
   * @param param0
   * @param param1
   */
  async login(
    { commit, dispatch }: ActionContext<UserState, RootState>,
    { email, password }: { email: string; password: string }
  ) {
    try {
      const resp: JwtDTO = await authSvc.login(email, password);
      if (resp.status === 'error') {
        Message.error(resp.message as string);
      } else {
        commit(USER_LOGIN, resp);
        router.push({ name: 'dashboard' });
      }
    } catch (e) {
      dispatch('logout');
    }
  },
  async register(
    { commit }: ActionContext<UserState, RootState>,
    registerDTO: RegisterDTO
  ) {
    const resp: RegisterResponse = await authSvc.register(registerDTO);
    commit(USER_UPDATE_REGISTER_INFO, {
      service: registerDTO.service,
      planId: registerDTO.planId,
      subdomain: registerDTO.subdomain,
      token: resp.payment_token
    });
    return resp;
  },
  async subscribe(
    { commit }: ActionContext<UserState, RootState>,
    subscribeDTO: SubscribeDTO
  ) {
    const resp: Response = await authSvc.subscribe(subscribeDTO);
    return resp;
  },
  async join(
    { commit }: ActionContext<UserState, RootState>,
    joinDTO: JoinDTO
  ) {
    const resp: JwtDTO = await authSvc.join(joinDTO);

    if (resp.status === 'error') {
      Message.error(resp.message as string);
    } else {
      commit(USER_LOGIN, resp);
      router.push({ name: 'dashboard' });
    }
  },
  async reset(
    { commit }: ActionContext<UserState, RootState>,
    resetDTO: ResetDTO
  ) {
    const resp: JwtDTO = await authSvc.reset(resetDTO);

    if (resp.status === 'error') {
      Message.error(resp.message as string);
    } else {
      commit(USER_LOGIN, resp);
      router.push({ name: 'dashboard' });
    }
  },
  /**
   * logout action
   * @param param0
   */
  logout({ commit }: ActionContext<UserState, RootState>) {
    commit(USER_LOGOUT);
    router.push({ name: 'login' });
  },
  /**
   * getJwtToken action
   * @param param0
   */
  async getJwtToken({ state, dispatch }: ActionContext<UserState, RootState>) {
    if (await dispatch('validateJwtToken')) {
      return state.jwt!.token;
    }
  },
  /**
   * loadJwtToken action
   * @param param0
   */
  async loadJwtToken({
    commit,
    dispatch
  }: ActionContext<UserState, RootState>) {
    const jwt: Jwt = LocalStorageService.get('jwt');
    const user: User = LocalStorageService.get('user');
    if (jwt && jwt.token && jwt.exp && user) {
      const dto: JwtDTO = {
        user,
        ...jwt
      };
      commit(USER_LOGIN, dto);
    }
  },
  /**
   * validateJwtToken action
   * @param param0
   */
  validateJwtToken({
    state,
    commit
  }: ActionContext<UserState, RootState>): boolean {
    const isValid = authSvc.validateJwt(state.jwt as Jwt);
    if (!isValid) {
      commit(USER_LOGOUT);
      router.push({ name: 'login' });
    }
    return isValid;
  },
  async initRoles({
    commit
  }: ActionContext<UserState, RootState>): Promise<void> {
    const roles: Role[] = await userSvc.getRoles();
    commit(USER_UPDATE_ROLES, roles);
  },
  async updateUser(
    { commit, state }: ActionContext<UserState, RootState>,
    payload: User
  ): Promise<User> {
    const user: User = await userSvc.updateUser(payload);
    if (state.user!.id === user.id) {
      commit(USER_UPDATE_USER, user);
    }
    return user;
  },
  async updatePassword(
    { commit, state }: ActionContext<UserState, RootState>,
    payload: UpdatePasswordDTO
  ): Promise<JwtDTO> {
    const dto: JwtDTO = await userSvc.updatePassword(payload);
    if (state.user!.id === payload.id && dto.token) {
      const jwt: Jwt = {
        token: dto.token,
        exp: dto.exp
      };
      commit(USER_UPDATE_JWT, jwt);
    }
    return dto;
  }
};

const mutations = {
  [USER_LOGIN](state: UserState, dto: JwtDTO) {
    const jwt: Jwt = {
      token: dto.token,
      exp: dto.exp
    };
    state.user = dto.user;
    state.jwt = jwt;
    LocalStorageService.set('jwt', jwt);
    LocalStorageService.set('user', dto.user);
  },
  [USER_UPDATE_JWT](state: UserState, jwt: Jwt) {
    state.jwt = jwt;
    LocalStorageService.set('jwt', jwt);
  },
  [USER_LOGOUT](state: UserState) {
    state.user = null;
    state.jwt = null;
    LocalStorageService.remove('jwt');
    LocalStorageService.remove('user');
  },
  [USER_UPDATE_ROLES](state: UserState, roles: Role[]) {
    state.roles = roles;
  },
  [USER_UPDATE_USER](state: UserState, user: User) {
    state.user = user;
  },
  [USER_UPDATE_REGISTER_INFO](state: UserState, registerInfo: RegisterInfo) {
    state.registerInfo = registerInfo;
  }
};

export default {
  state: modState,
  getters,
  actions,
  mutations
};
